import { z } from 'zod';
import { IsoDateTime, Role, Uuid } from './common';

export const LoginBody = z
  .object({
    email: z.email().trim().toLowerCase(),
    password: z.string().min(1).max(200),
    remember: z
      .boolean()
      .default(true)
      .describe('"Manter conectado neste aparelho": 30-day cookie.'),
    captchaToken: z
      .string()
      .min(1)
      .max(4096)
      .optional()
      .describe(
        'Response token of the captcha widget. Required after repeated failures, when the API ' +
          'answers `captcha_required` (details: `CaptchaChallenge`).',
      ),
    otp: z
      .string()
      .trim()
      .min(6)
      .max(20)
      .optional()
      .describe(
        'Two-step verification: the 6-digit authenticator code or a recovery code (XXXX-XXXX). ' +
          'Required when the API answers `two_factor_required`.',
      ),
  })
  .meta({ example: { email: 'lucas@example.com', password: '••••••••', remember: true } });
export type LoginBody = z.input<typeof LoginBody>;

export const CaptchaProvider = z.enum(['recaptcha', 'turnstile']);
export type CaptchaProvider = z.infer<typeof CaptchaProvider>;

/** `details.captcha` on login errors when the next attempt must carry a captcha token. */
export const CaptchaChallenge = z
  .object({ provider: CaptchaProvider, siteKey: z.string() })
  .meta({ id: 'CaptchaChallenge' });
export type CaptchaChallenge = z.infer<typeof CaptchaChallenge>;

export const Me = z
  .object({
    id: Uuid,
    name: z.string(),
    email: z.email(),
    role: Role,
  })
  .meta({ id: 'Me' });
export type Me = z.infer<typeof Me>;

export const SessionInfo = z
  .object({
    user: Me,
    csrfToken: z
      .string()
      .describe('Send as `X-CSRF-Token` on unsafe requests made with the session cookie.'),
    expiresAt: IsoDateTime,
    persistent: z.boolean(),
  })
  .meta({ id: 'SessionInfo' });
export type SessionInfo = z.infer<typeof SessionInfo>;

export const PASSWORD_MIN = 10;

export const SetPasswordBody = z.object({
  token: z.string().min(16),
  name: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .optional()
    .describe('Invite only: lets the person fix their name.'),
  password: z.string().min(PASSWORD_MIN).max(200),
});
export type SetPasswordBody = z.infer<typeof SetPasswordBody>;

export const AuthLinkInfo = z.object({
  kind: z.enum(['invite', 'password_reset']),
  email: z.email(),
  name: z.string(),
  expiresAt: IsoDateTime,
});
export type AuthLinkInfo = z.infer<typeof AuthLinkInfo>;

export const TwoFactorStatus = z
  .object({
    available: z
      .boolean()
      .describe(
        'False when the server has no AUTH_SECRET: two-step verification cannot be turned on.',
      ),
    enabled: z.boolean(),
    enabledAt: IsoDateTime.nullable(),
    recoveryCodesLeft: z.number().int(),
  })
  .meta({ id: 'TwoFactorStatus' });
export type TwoFactorStatus = z.infer<typeof TwoFactorStatus>;

export const TwoFactorCode = z
  .string()
  .trim()
  .min(6)
  .max(20)
  .describe('6-digit authenticator code, or a recovery code where accepted.');

export const TwoFactorSetupBody = z.object({ password: z.string().min(1).max(200) });
export type TwoFactorSetupBody = z.infer<typeof TwoFactorSetupBody>;

export const TwoFactorSetup = z
  .object({
    secret: z
      .string()
      .describe('Base32 key, for typing into the app when the QR code cannot be read.'),
    otpauthUri: z.string().describe('`otpauth://totp/...` link to show as a QR code.'),
  })
  .meta({ id: 'TwoFactorSetup' });
export type TwoFactorSetup = z.infer<typeof TwoFactorSetup>;

export const TwoFactorEnableBody = z.object({ code: TwoFactorCode });
export type TwoFactorEnableBody = z.infer<typeof TwoFactorEnableBody>;

/** Password plus a current code (or a recovery code): to turn 2FA off or get new recovery codes. */
export const TwoFactorConfirmBody = z.object({
  password: z.string().min(1).max(200),
  code: TwoFactorCode,
});
export type TwoFactorConfirmBody = z.infer<typeof TwoFactorConfirmBody>;

export const RecoveryCodes = z
  .object({
    recoveryCodes: z
      .array(z.string())
      .describe('10 one-time codes (XXXX-XXXX) for when the phone is lost. Shown only once.'),
  })
  .meta({ id: 'RecoveryCodes' });
export type RecoveryCodes = z.infer<typeof RecoveryCodes>;

export const LoginResult = z.enum([
  'success',
  'bad_password',
  'unknown_email',
  'inactive',
  'captcha_failed',
  'two_factor_failed',
]);
export type LoginResult = z.infer<typeof LoginResult>;

export const LoginAttempt = z
  .object({
    id: Uuid,
    at: IsoDateTime,
    email: z.string().describe('As typed (lowercased); may belong to no account.'),
    ip: z.string(),
    device: z.string().describe('Short label from the user agent, e.g. "Chrome · macOS".'),
    userId: Uuid.nullable(),
    userName: z.string().nullable(),
    result: LoginResult,
  })
  .meta({ id: 'LoginAttempt' });
export type LoginAttempt = z.infer<typeof LoginAttempt>;

export const LoginAttemptQuery = z.object({
  failures: z.enum(['true', 'false']).optional().describe('Only failed attempts.'),
});
export const LoginAttemptList = z
  .object({ data: z.array(LoginAttempt), nextCursor: z.string().nullable() })
  .meta({ id: 'LoginAttemptList' });
export type LoginAttemptList = z.infer<typeof LoginAttemptList>;
